// PricingDataSourcesPanel: transparency block explaining where pricing data comes from
// and what was actually collected this week (verified vs unverified counts, breakdowns).

import React from 'react';
import type { Lang } from '../lib/i18n';
import { categoryLabel } from '../lib/i18n';
import type { Plan, Provider } from '../lib/api';

interface PricingDataSourcesPanelProps {
  plans: Plan[];
  providers: Provider[];
  lang: Lang;
}

export function PricingDataSourcesPanel({ plans, providers, lang }: PricingDataSourcesPanelProps) {
  const providerMap = Object.fromEntries(providers.map((p) => [p.id, p]));

  // Stats
  const total = plans.length;
  const verified = plans.filter((p) => p.is_verified === 1).length;
  const priced = plans.filter((p) => p.price_usd != null).length;
  const withGb = plans.filter((p) => p.data_gb != null).length;
  const withSource = plans.filter((p) => p.source_url && p.source_url.startsWith('http')).length;

  // Plans per category
  const byCategory: Record<string, number> = { consumer_esim: 0, corporate_iot: 0, airline_wifi: 0 };
  for (const p of plans) {
    const cat = providerMap[p.provider_id]?.category;
    if (cat && byCategory[cat] != null) byCategory[cat]++;
  }

  // Top source domains
  const domainCounts = new Map<string, number>();
  for (const p of plans) {
    if (p.source_url && p.source_url.startsWith('http')) {
      try {
        const d = new URL(p.source_url).hostname.replace(/^www\./, '');
        domainCounts.set(d, (domainCounts.get(d) ?? 0) + 1);
      } catch { /* ignore */ }
    }
  }
  const topDomains = Array.from(domainCounts.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8);

  // Providers without any priced plan
  const providersWithPricedPlan = new Set(plans.filter((p) => p.price_usd != null).map((p) => p.provider_id));
  const providersNoData = providers.filter((p) => !providersWithPricedPlan.has(p.id));

  return (
    <section>
      <h2 className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-3">
        {lang === 'tr' ? 'FİYAT VERİSİ KAYNAKLARI' : 'PRICING DATA SOURCES'}
      </h2>

      <div className="card p-4 space-y-4">
        {/* How it works */}
        <div className="border-l-2 border-emerald-500/40 pl-3 py-1">
          <div className="text-xs font-medium text-neutral-800 mb-1">
            {lang === 'tr' ? 'Veri nereden geliyor?' : 'Where does this data come from?'}
          </div>
          <div className="text-xs text-neutral-600 leading-relaxed">
            {lang === 'tr'
              ? 'Her sağlayıcı için Perplexity Sonar üzerinden yapısal bir sorgu çalıştırılır. Sonar canlı arama sonuçlarını okuyup plan adı, bölge, veri miktarı (GB), geçerlilik (gün), USD fiyat ve kaynak URL\'sini JSON olarak döndürür. Sonuçlar "doğrulanmış" (Sonar yapısal alanları döndürdü) ve "doğrulanmamış" (eksik alan) olarak işaretlenir. Kurumsal & IoT ile Uçak İçi Bağlantı sağlayıcılarının çoğunluğu kamuya açık tüketici fiyatı yayınlamaz; bu kayıtlar bilinçli olarak boş bırakılır.'
              : 'For each provider we run a structured query through Perplexity Sonar. Sonar reads live search results and returns plan name, region, data (GB), validity (days), USD price and source URL as JSON. Records are marked "verified" (Sonar returned structured fields) or "unverified" (fields missing). Most Corporate & IoT and In-Flight Connectivity providers do not publish public consumer pricing; those rows are intentionally left blank.'}
          </div>
        </div>

        {/* Stat grid */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
          <Stat label={lang === 'tr' ? 'Toplam plan' : 'Total plans'} value={total} />
          <Stat label={lang === 'tr' ? 'Doğrulanmış' : 'Verified'} value={verified} sub={`${total > 0 ? Math.round((verified / total) * 100) : 0}%`} />
          <Stat label={lang === 'tr' ? 'Fiyatlı' : 'Priced'} value={priced} />
          <Stat label={lang === 'tr' ? 'GB metadata' : 'With GB data'} value={withGb} />
          <Stat label={lang === 'tr' ? 'Kaynak URL' : 'Source URL'} value={withSource} />
        </div>

        {/* By category */}
        <div>
          <div className="text-xs font-medium text-neutral-600 mb-2">
            {lang === 'tr' ? 'Kategoriye göre dağılım' : 'Distribution by category'}
          </div>
          <div className="grid sm:grid-cols-3 gap-2">
            {Object.entries(byCategory).map(([cat, count]) => (
              <div key={cat} className="flex items-center justify-between text-xs py-1.5 px-2 rounded border border-neutral-200">
                <span className="text-neutral-700">{categoryLabel(cat, lang)}</span>
                <span className="tabular-nums text-neutral-600">{count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Top source domains */}
        {topDomains.length > 0 && (
          <div>
            <div className="text-xs font-medium text-neutral-600 mb-2">
              {lang === 'tr' ? 'En çok atıf yapılan kaynaklar' : 'Most-cited sources'}
            </div>
            <div className="grid gap-1.5">
              {topDomains.map(([domain, count]) => (
                <div key={domain} className="flex items-center justify-between text-xs py-1.5 px-2 rounded border border-neutral-200">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500/60 shrink-0" />
                    <span className="text-neutral-800 truncate">{domain}</span>
                  </div>
                  <span className="tabular-nums text-neutral-600 shrink-0">
                    {count.toLocaleString(lang === 'tr' ? 'tr-TR' : 'en-US')} {lang === 'tr' ? 'plan' : 'plans'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Providers without priced data */}
        {providersNoData.length > 0 && (
          <div>
            <div className="text-xs font-medium text-neutral-600 mb-2">
              {lang === 'tr'
                ? `Tüketici fiyatı yayınlamayan sağlayıcılar (${providersNoData.length})`
                : `Providers without public consumer pricing (${providersNoData.length})`}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {providersNoData.map((p) => (
                <span
                  key={p.id}
                  className="text-[11px] px-2 py-0.5 rounded border border-neutral-200 text-neutral-500"
                  title={p.category}
                >
                  {p.name}
                </span>
              ))}
            </div>
            <div className="text-[11px] text-neutral-500 mt-1.5 leading-relaxed">
              {lang === 'tr'
                ? 'Bu sağlayıcılar genellikle havayolu/kurumsal müşteriye doğrudan fiyat verir; ihale ve sözleşme bazlıdır.'
                : 'These providers typically price directly to airline/enterprise customers; pricing is tender- and contract-based.'}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function Stat({ label, value, sub }: { label: string; value: number | string; sub?: string }) {
  return (
    <div>
      <div className="text-lg font-semibold text-neutral-900 tabular-nums">{value}</div>
      <div className="text-[10px] text-neutral-500 uppercase tracking-wider">{label}</div>
      {sub && <div className="text-[10px] text-neutral-500 tabular-nums">{sub}</div>}
    </div>
  );
}
